import Session from '../core/session'
import Config from '../core/config'

interface APICallData {
  endpoint: string
  method: string
  status_code: number
  response_time: number
  request_size?: number
  response_size?: number
  error?: string
}

export class APICollector {
  private static instance: APICollector
  private isTracking: boolean = false
  private originalFetch: typeof fetch
  private originalXHROpen: typeof XMLHttpRequest.prototype.open
  private originalXHRSend: typeof XMLHttpRequest.prototype.send

  private constructor() {
    this.originalFetch = window.fetch
    this.originalXHROpen = XMLHttpRequest.prototype.open
    this.originalXHRSend = XMLHttpRequest.prototype.send
  }

  public static getInstance(): APICollector {
    if (!APICollector.instance) {
      APICollector.instance = new APICollector()
    }
    return APICollector.instance
  }

  /**
   * Start intercepting API calls
   */
  public start(): void {
    if (this.isTracking) {
      return
    }

    this.isTracking = true

    // Intercept fetch API
    this.interceptFetch()

    // Intercept XMLHttpRequest
    this.interceptXHR()
  }

  /**
   * Intercept fetch requests
   */
  private interceptFetch(): void {
    const self = this

    window.fetch = async function (...args: Parameters<typeof fetch>): Promise<Response> {
      const startTime = performance.now()
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url || args[0].toString()
      const method = (args[1]?.method || 'GET').toUpperCase()

      // Calculate request size
      let requestSize = 0
      if (args[1]?.body) {
        try {
          requestSize = new Blob([args[1].body as BlobPart]).size
        } catch {
          requestSize = 0 // Fallback if body can't be measured
        }
      }

      try {
        const response = await self.originalFetch.apply(window, args)
        const endTime = performance.now()
        const responseTime = Math.round(endTime - startTime)

        // Clone response to read body without consuming it
        const clonedResponse = response.clone()
        let responseSize = 0

        try {
          const text = await clonedResponse.text()
          responseSize = new Blob([text]).size
        } catch (e) {
          // Ignore if can't read body
        }

        // Track API call
        self.trackAPICall({
          endpoint: self.sanitizeURL(url),
          method: method,
          status_code: response.status,
          response_time: responseTime,
          request_size: requestSize,
          response_size: responseSize
        })

        // Track error if status >= 400
        if (response.status >= 400) {
          self.trackAPIError({
            endpoint: self.sanitizeURL(url),
            method: method,
            status_code: response.status,
            response_time: responseTime,
            error: `HTTP ${response.status}: ${response.statusText}`
          })
        }

        return response
      } catch (error: any) {
        const endTime = performance.now()
        const responseTime = Math.round(endTime - startTime)

        // Track network error
        self.trackAPIError({
          endpoint: self.sanitizeURL(url),
          method: method,
          status_code: 0,
          response_time: responseTime,
          error: error.message || 'Network error'
        })

        throw error
      }
    }
  }

  /**
   * Intercept XMLHttpRequest
   */
  private interceptXHR(): void {
    const self = this

    XMLHttpRequest.prototype.open = function (
      method: string,
      url: string | URL,
      ...args: any[]
    ): void {
      // Store request info
      (this as any).__radarsnap_method = method.toUpperCase()
      ;(this as any).__radarsnap_url = typeof url === 'string' ? url : url.toString()
      ;(this as any).__radarsnap_start = performance.now()

      return self.originalXHROpen.apply(this, [method, url, ...args] as any)
    }

    XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit): void {
      const xhr = this
      const method = (xhr as any).__radarsnap_method || 'GET'
      const url = (xhr as any).__radarsnap_url || ''
      const startTime = (xhr as any).__radarsnap_start || performance.now()

      // Calculate request size
      let requestSize = 0
      if (body) {
        try {
          requestSize = new Blob([body as BlobPart]).size
        } catch {
          requestSize = 0 // Fallback if body can't be measured
        }
      }

      // Track on load
      xhr.addEventListener('load', () => {
        const endTime = performance.now()
        const responseTime = Math.round(endTime - startTime)
        const responseSize = xhr.responseText ? new Blob([xhr.responseText]).size : 0

        self.trackAPICall({
          endpoint: self.sanitizeURL(url),
          method: method,
          status_code: xhr.status,
          response_time: responseTime,
          request_size: requestSize,
          response_size: responseSize
        })

        // Track error if status >= 400
        if (xhr.status >= 400) {
          self.trackAPIError({
            endpoint: self.sanitizeURL(url),
            method: method,
            status_code: xhr.status,
            response_time: responseTime,
            error: `HTTP ${xhr.status}: ${xhr.statusText}`
          })
        }
      })

      // Track network errors
      xhr.addEventListener('error', () => {
        const endTime = performance.now()
        const responseTime = Math.round(endTime - startTime)

        self.trackAPIError({
          endpoint: self.sanitizeURL(url),
          method: method,
          status_code: 0,
          response_time: responseTime,
          error: 'Network error'
        })
      })

      return self.originalXHRSend.apply(xhr, [body] as any)
    }
  }

  /**
   * Track successful API call
   */
  private trackAPICall(data: APICallData): void {
    // Don't track calls to our own analytics endpoint
    if (data.endpoint.includes(Config.get('endpoint'))) {
      return
    }

    Session.trackEvent('api_call', data)

    if (Config.isDebug()) {
      console.log('[RadarSnap] API call tracked:', data.method, data.endpoint, data.status_code)
    }
  }

  /**
   * Track API error
   */
  private trackAPIError(data: APICallData): void {
    // Don't track calls to our own analytics endpoint
    if (data.endpoint.includes(Config.get('endpoint'))) {
      return
    }

    Session.trackEvent('api_error', {
      ...data,
      error_type: data.status_code >= 500 ? 'server_error' : data.status_code >= 400 ? 'client_error' : 'network_error'
    })

    if (Config.isDebug()) {
      console.error('[RadarSnap] API error tracked:', data.method, data.endpoint, data.error)
    }
  }

  /**
   * Sanitize URL (remove query params with sensitive data)
   */
  private sanitizeURL(url: string): string {
    try {
      const urlObj = new URL(url, window.location.origin)

      // Remove sensitive query parameters
      const sensitiveParams = ['token', 'api_key', 'password', 'secret', 'auth']
      sensitiveParams.forEach(param => {
        if (urlObj.searchParams.has(param)) {
          urlObj.searchParams.set(param, '[REDACTED]')
        }
      })

      return urlObj.pathname + (urlObj.search ? urlObj.search : '')
    } catch (e) {
      // If URL parsing fails, return as-is
      return url
    }
  }

  /**
   * Stop intercepting
   */
  public stop(): void {
    this.isTracking = false
    window.fetch = this.originalFetch
    XMLHttpRequest.prototype.open = this.originalXHROpen
    XMLHttpRequest.prototype.send = this.originalXHRSend
  }
}

export default APICollector.getInstance()